import { useMemo, useState } from 'react';
import { Filter, SlidersHorizontal, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { MultiSelect } from '@/components/ui/multiselect';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { useFilters } from '@/contexts/FilterContext';
import { useScheduler } from '@/contexts/SchedulerContext';
import { AdvancedFilterDialog } from './AdvancedFilterDialog';
import { PROJECT_STATUSES } from '@/lib/statuses';

// ─── Trigger Button ───────────────────────────────────────────────────────────

interface CalendarFilterDrawerTriggerProps {
  activeFilterCount: number;
  onClick: () => void;
}

export function CalendarFilterDrawerTrigger({
  activeFilterCount,
  onClick,
}: CalendarFilterDrawerTriggerProps) {
  return (
    <Button
      variant={activeFilterCount > 0 ? 'default' : 'outline'}
      size="sm"
      onClick={onClick}
      className="h-8 w-8 p-0"
      title={
        activeFilterCount > 0
          ? `${activeFilterCount} filter${activeFilterCount !== 1 ? 's' : ''} active`
          : 'Filters'
      }
    >
      <Filter className="h-3.5 w-3.5" />
    </Button>
  );
}

// ─── Simple filter field ──────────────────────────────────────────────────────

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold text-foreground">{label}</Label>
      {children}
    </div>
  );
}

// ─── Section heading (for standalone labels like Utilisation / Advanced) ─────

function SectionHeading({ label }: { label: string }) {
  return (
    <Label className="text-xs font-bold text-foreground block">{label}</Label>
  );
}

// ─── Main Drawer ──────────────────────────────────────────────────────────────

interface CalendarFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CalendarFilterDrawer({ open, onOpenChange }: CalendarFilterDrawerProps) {
  const {
    filters,
    updateFilter,
    resetFilters,
    hasActiveFilters,
    filterStats,
    hasActiveAdvancedFilters,
    advancedFilters,
    resetAdvancedFilters,
    showFilteredBookings,
    setShowFilteredBookings,
    hasHiddenBookings,
  } = useFilters();

  const { settings, resources, projectDetails, costCentres } = useScheduler();
  const [advancedDialogOpen, setAdvancedDialogOpen] = useState(false);

  // ── Derived options ────────────────────────────────────────────────────────

  const groupField =
    settings.groupingMethod === 'scheduler-group' ? 'schedulerGroup' : 'department';
  const groupLabel =
    settings.groupingMethod === 'scheduler-group' ? 'Scheduler Groups' : 'Departments';

  const DEPARTMENTS = useMemo(() => {
    let base = resources;
    if (filters.roles.length > 0) base = base.filter((r) => filters.roles.includes(r.role));
    if (filters.resources.length > 0) base = base.filter((r) => filters.resources.includes(r.id));
    const unique = new Set<string>();
    base.forEach((r) => {
      const v = r[groupField as keyof typeof r] as string | undefined;
      if (v) unique.add(v);
    });
    return Array.from(unique).sort().map((v) => ({ value: v, label: v }));
  }, [resources, filters.roles, filters.resources, groupField]);

  const AVAILABLE_RESOURCES = useMemo(() => {
    let base = resources;
    if (filters.departments.length > 0)
      base = base.filter((r) => {
        const v = r[groupField as keyof typeof r] as string | undefined;
        return v && filters.departments.includes(v);
      });
    if (filters.roles.length > 0) base = base.filter((r) => filters.roles.includes(r.role));
    return base
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((r) => ({ value: r.id, label: r.name }));
  }, [resources, filters.departments, filters.roles, groupField]);

  const AVAILABLE_ROLES = useMemo(() => {
    let base = resources;
    if (filters.departments.length > 0)
      base = base.filter((r) => {
        const v = r[groupField as keyof typeof r] as string | undefined;
        return v && filters.departments.includes(v);
      });
    if (filters.resources.length > 0)
      base = base.filter((r) => filters.resources.includes(r.id));
    const unique = new Set<string>();
    base.forEach((r) => { if (r.role) unique.add(r.role); });
    return Array.from(unique).sort().map((v) => ({ value: v, label: v }));
  }, [resources, filters.departments, filters.resources, groupField]);

  const AVAILABLE_PROJECTS = useMemo(() => {
    let opts = projectDetails
      .map((p) => ({
        value: String(p.id),
        label: p.name && p.billingCode ? `${p.name} (${p.billingCode})` : p.name || String(p.id),
        status: p.status,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (filters.projectStatuses.length > 0)
      opts = opts.filter((o) => o.status && filters.projectStatuses.includes(o.status));
    opts.unshift({ value: 'no-project', label: 'No Project assigned' } as typeof opts[number]);
    return opts;
  }, [projectDetails, filters.projectStatuses]);

  const AVAILABLE_COST_CENTRES = useMemo(() => {
    let base = costCentres;
    const selectedProjectIds = filters.projects.filter((p) => p !== 'no-project');
    if (selectedProjectIds.length > 0)
      base = base.filter(
        (cc) => cc.projectId && selectedProjectIds.includes(String(cc.projectId))
      );
    let opts = base
      .map((cc) => ({
        value: String(cc.id),
        label:
          cc.name && cc.code ? `${cc.name} (${cc.code})` : cc.name || cc.code || String(cc.id),
        status: cc.status,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (filters.costCentreStatuses.length > 0)
      opts = opts.filter((o) => o.status && filters.costCentreStatuses.includes(o.status));
    opts.unshift({
      value: 'no-cost-centre',
      label: 'No Cost Centre assigned',
    } as typeof opts[number]);
    return opts;
  }, [costCentres, filters.projects, filters.costCentreStatuses]);

  const AVAILABLE_BOOKING_TYPES = useMemo(() => {
    const opts = (settings.bookingTypes || [])
      .map((bt) => ({ value: String(bt.id), label: `[${bt.code}] ${bt.name}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
    opts.unshift({ value: 'no-booking-type', label: 'No Booking Type assigned' });
    return opts;
  }, [settings.bookingTypes]);

  const BOOKING_STATUSES = useMemo(
    () =>
      settings.statusConfigs
        .filter((s) => s.enabled)
        .map((s) => ({ value: String(s.id), label: s.customText || s.text })),
    [settings.statusConfigs]
  );

  const PROJECT_STATUS_OPTIONS = useMemo(
    () => PROJECT_STATUSES.map((s) => ({ value: s.value, label: s.label })),
    []
  );

  // ── Active filter count ────────────────────────────────────────────────────

  const isUtilisationActive =
    filters.utilisationRange[0] !== 0 ||
    filters.utilisationRange[1] !== 100 ||
    filters.overUtilisedOnly;

  const activeFilterCount =
    filters.departments.length +
    filters.resources.length +
    filters.roles.length +
    filters.projects.length +
    filters.costCentres.length +
    filters.bookingTypes.length +
    filters.bookingStatuses.length +
    (isUtilisationActive ? 1 : 0) +
    (hasActiveAdvancedFilters ? advancedFilters.conditions.length : 0);

  const canClearAll = hasActiveFilters || hasActiveAdvancedFilters;

  // Track which sections are visible to know where to place dividers
  const showSchedulerGroups = DEPARTMENTS.length > 0;
  const showRoles = AVAILABLE_ROLES.length > 0;
  const showResources = AVAILABLE_RESOURCES.length > 0;
  const showProjectStatus = AVAILABLE_PROJECTS.length > 0;
  const showProjects = AVAILABLE_PROJECTS.length > 0;
  const showCostCentres = AVAILABLE_COST_CENTRES.length > 0;
  const showBookingType = AVAILABLE_BOOKING_TYPES.length > 0;
  const showBookingStatus = BOOKING_STATUSES.length > 0;

  // A helper to decide if a divider is needed between two sections
  const resourcesSectionVisible = showSchedulerGroups || showRoles || showResources;
  const projectsSectionVisible = showProjectStatus || showProjects;
  const bookingsSectionVisible = showBookingType || showBookingStatus;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-[380px] sm:max-w-[380px] flex flex-col p-0 gap-0"
        >
          {/* Header */}
          <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0 gap-0">
            <div className="flex items-center gap-2">
              <SheetTitle className="text-sm font-semibold">Filters</SheetTitle>
              <Badge
                variant="secondary"
                className="flex items-center gap-1 px-2.5 py-0.5 h-6 text-[11px] font-medium rounded-full bg-primary/10 text-primary border-0 hover:bg-primary/10"
              >
                <Filter className="h-3 w-3" />
                {activeFilterCount} filters active
              </Badge>
            </div>

            {/* Result counts — only when filters are active */}
            {filterStats && (hasActiveFilters || hasActiveAdvancedFilters) && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {filterStats.filteredResources} of {filterStats.totalResources} resources
                &nbsp;·&nbsp;
                {filterStats.filteredBookings} of {filterStats.totalBookings} bookings
              </p>
            )}
          </SheetHeader>

          {/* Scrollable filter list */}
          <ScrollArea className="flex-1">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-4">

                {/* ── Resources group ── */}
                {showSchedulerGroups && (
                  <Field label={groupLabel}>
                    <MultiSelect
                      options={DEPARTMENTS}
                      value={filters.departments}
                      onValueChange={(v) => updateFilter('departments', v)}
                      placeholder={`All ${groupLabel}`}
                    />
                  </Field>
                )}

                {showRoles && (
                  <Field label="Roles">
                    <MultiSelect
                      options={AVAILABLE_ROLES}
                      value={filters.roles}
                      onValueChange={(v) => updateFilter('roles', v)}
                      placeholder="All Roles"
                    />
                  </Field>
                )}

                {showResources && (
                  <Field label="Resources">
                    <MultiSelect
                      options={AVAILABLE_RESOURCES}
                      value={filters.resources}
                      onValueChange={(v) => updateFilter('resources', v)}
                      placeholder="All Resources"
                    />
                  </Field>
                )}

                {/* Divider after Resources group */}
                {resourcesSectionVisible && (projectsSectionVisible || showCostCentres || bookingsSectionVisible) && (
                  <Separator />
                )}

                {/* ── Projects group ── */}
                {showProjectStatus && (
                  <Field label="Project Status">
                    <MultiSelect
                      options={PROJECT_STATUS_OPTIONS}
                      value={filters.projectStatuses}
                      onValueChange={(v) => updateFilter('projectStatuses', v)}
                      placeholder="All Statuses"
                    />
                  </Field>
                )}

                {showProjects && (
                  <Field label="Projects">
                    <MultiSelect
                      options={AVAILABLE_PROJECTS}
                      value={filters.projects}
                      onValueChange={(v) => {
                        updateFilter('projects', v);
                        if (filters.costCentres.length > 0) updateFilter('costCentres', []);
                      }}
                      placeholder="All Projects"
                    />
                  </Field>
                )}

                {/* Divider after Projects group */}
                {projectsSectionVisible && (showCostCentres || bookingsSectionVisible) && (
                  <Separator />
                )}

                {/* ── Cost Centres ── */}
                {showCostCentres && (
                  <Field label="Cost Centres">
                    <MultiSelect
                      options={AVAILABLE_COST_CENTRES}
                      value={filters.costCentres}
                      onValueChange={(v) => updateFilter('costCentres', v)}
                      placeholder="All Cost Centres"
                    />
                  </Field>
                )}

                {/* Divider after Cost Centres */}
                {showCostCentres && bookingsSectionVisible && (
                  <Separator />
                )}

                {/* ── Bookings group ── */}
                {showBookingType && (
                  <Field label="Booking Type">
                    <MultiSelect
                      options={AVAILABLE_BOOKING_TYPES}
                      value={filters.bookingTypes}
                      onValueChange={(v) => updateFilter('bookingTypes', v)}
                      placeholder="All Booking Types"
                    />
                  </Field>
                )}

                {showBookingStatus && (
                  <Field label="Booking Status">
                    <MultiSelect
                      options={BOOKING_STATUSES}
                      value={filters.bookingStatuses}
                      onValueChange={(v) => updateFilter('bookingStatuses', v)}
                      placeholder="All Statuses"
                    />
                  </Field>
                )}

                {/* Divider before Utilisation */}
                {(resourcesSectionVisible || projectsSectionVisible || showCostCentres || bookingsSectionVisible) && (
                  <Separator />
                )}

                {/* ── Utilisation ── */}
                <div className="space-y-3 pt-1">
                  <SectionHeading label="Utilisation" />

                  {/* Over-utilised toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground">Over utilised only</span>
                    <Switch
                      checked={filters.overUtilisedOnly}
                      onCheckedChange={(v) => updateFilter('overUtilisedOnly', v)}
                    />
                  </div>

                  {/* Range slider */}
                  <div className="space-y-2 px-0.5">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Range</span>
                      <span className="tabular-nums font-medium text-foreground">
                        {filters.overUtilisedOnly
                          ? '≥ 100%'
                          : `${filters.utilisationRange[0]}% – ${filters.utilisationRange[1]}%`}
                      </span>
                    </div>
                    <Slider
                      min={0}
                      max={100}
                      step={5}
                      value={filters.utilisationRange as [number, number]}
                      onValueChange={(v) => updateFilter('utilisationRange', [v[0], v[1]])}
                      disabled={filters.overUtilisedOnly}
                      className="w-full"
                    />
                    <div className="flex justify-between text-[11px] text-muted-foreground/60">
                      <span>0%</span>
                      <span>100%</span>
                    </div>
                  </div>
                </div>

                {/* Divider before Advanced */}
                <Separator />

                {/* ── Advanced ── */}
                <div className="pt-1 space-y-2">
                  <SectionHeading label="Advanced" />

                  <Button
                    variant={hasActiveAdvancedFilters ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setAdvancedDialogOpen(true)}
                    className="w-full justify-between h-8 text-xs"
                  >
                    <span className="flex items-center gap-1.5">
                      <SlidersHorizontal className="h-3.5 w-3.5" />
                      Advanced Filters
                    </span>
                    {hasActiveAdvancedFilters && (
                      <Badge
                        variant="secondary"
                        className="h-4 min-w-4 px-1 rounded-full text-[10px]"
                      >
                        {advancedFilters.conditions.length}
                      </Badge>
                    )}
                  </Button>

                  {hasHiddenBookings && (
                    <div className="flex items-center justify-between py-1">
                      <span className="text-sm text-foreground">Show filtered bookings</span>
                      <Switch
                        checked={showFilteredBookings}
                        onCheckedChange={setShowFilteredBookings}
                      />
                    </div>
                  )}
                </div>

              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>

          {/* Footer */}
          <SheetFooter className="px-4 py-3 border-t shrink-0 flex-row items-center justify-between gap-2">
            <Button
              variant="ghost"
              className="h-9 gap-1.5 text-muted-foreground hover:text-foreground px-2"
              onClick={() => { resetFilters(); resetAdvancedFilters(); }}
              disabled={!canClearAll}
            >
              <X className="h-3.5 w-3.5" />
              Clear All
            </Button>
            <Button className="h-9 gap-1.5 px-5" onClick={() => onOpenChange(false)}>
              <Check className="h-3.5 w-3.5" />
              Apply Filters
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AdvancedFilterDialog
        open={advancedDialogOpen}
        onOpenChange={setAdvancedDialogOpen}
      />
    </>
  );
}
